import { MouseEvent, useMemo } from 'react'
import { v4 as uuid } from 'uuid'

import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'

import { useAppDispatch } from '@hooks'
import {
  checkMaxOpenedWindowsCount,
  getCenteredPositionWindow,
  getInitialPositionWindow,
  transformCommonParametersToValues,
} from '@helpers'
import { ACTION_CODE, COMMON_PARAMETERS } from '@constants'
import { ActionResultType, ActionRow, DIALOG_WINDOW_TYPE, ObjectDataValue } from '@types'

import { MuiEvent } from '../../../../wiskey-react-table-component/src'

export const useOpenForm = () => {
  const dispatch = useAppDispatch()
  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const openForm = (
    action: ActionRow,
    srcObj: {
      formProps?: OpenFormProps
      event?: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>
      parentDialogId?: string | null
      additionalProps?: {
        addHistory?: boolean
      }
    } = {}
  ) => {
    const { formProps, event, parentDialogId, additionalProps } = srcObj

    if (!formProps || checkMaxOpenedWindowsCount(initialCommonParameters)) {
      return Promise.resolve({
        actionCode: ACTION_CODE.OPEN_FORM_MODEL,
        value: {
          opened: false,
          srcObj,
        },
      } as ActionResultType<Record<string, ObjectDataValue>>)
    }

    const { id } = formProps

    const globalId = uuid()

    const initialPosition = event ? getInitialPositionWindow(event) : getCenteredPositionWindow()

    if (additionalProps?.addHistory) {
      dispatch(
        addFormHistory({
          id: id || globalId,
          form: {
            ...formProps,
            isWindow: true,
            title: action.objectFormCode as string,
            objectId: formProps?.objectId || null,
            id: id || globalId,
            formCode: action.objectFormCode || '',
          },
        })
      )
    }

    dispatch(
      openFormDialog({
        id: id || globalId,
        parentDialogId: parentDialogId ?? null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          ...formProps,
          globalId: id || globalId,
          formCode: action.objectFormCode || '',
          objectCode: action.objectCode as string,
        },
        initialPosition,
      })
    )

    return Promise.resolve({
      actionCode: ACTION_CODE.OPEN_FORM_MODEL,
      value: {
        opened: true,
        srcObj,
      },
    } as ActionResultType<Record<string, ObjectDataValue>>)
  }

  return {
    openForm,
  }
}
